import axios from 'axios'
import Fuse from 'fuse.js'
import fuzzysort from 'fuzzysort'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import useSWR from 'swr'

import type { FullRecipe, Recipe } from './types'

export function useRecipes() {
  return useSWR<
    (Recipe & { lastConsumedAt: string; plans: [{ consumedAt: string }] })[]
  >('/api/recipes', (url: string) =>
    axios
      .get<
        (FullRecipe & {
          lastConsumedAt: string
          plans: [{ consumedAt: string }]
        })[]
      >(url)
      .then((res) =>
        res.data.map((r) => ({
          ...r,
          search:
            r.display +
            ' ' +
            r.main +
            ' ' +
            r.ingredients.map((i) => i.product.display).join(' '),
        }))
      )
  )
}

function useLocalStorage<T>(key: string, defaultValue: T) {
  // Create state variable to store
  // localStorage value in state
  const [localStorageValue, setLocalStorageValue] = useState(() => {
    try {
      const value = window.localStorage.getItem(key)
      // If value is already present in
      // localStorage then return it

      // Else set default value in
      // localStorage and then return it
      if (value) {
        return JSON.parse(value)
      } else {
        window.localStorage.setItem(key, JSON.stringify(defaultValue))
        return defaultValue
      }
    } catch (error) {
      return defaultValue
    }
  })

  // this method update our localStorage and our state
  const setLocalStorageStateValue: Dispatch<SetStateAction<T>> = (
    valueOrFn
  ) => {
    let newValue
    if (typeof valueOrFn === 'function') {
      const fn = valueOrFn as (prevState: T) => T
      newValue = fn(localStorageValue)
    } else {
      newValue = valueOrFn
    }
    window.localStorage.setItem(key, JSON.stringify(newValue))
    setLocalStorageValue(newValue)
  }
  return [localStorageValue, setLocalStorageStateValue] as const
}

export default function RecipeSelect({
  onChange,
  onClose,
  onPlan,
}: {
  onChange: () => void
  onClose: () => void
  onPlan: (input: {
    multiplier?: number
    recipeId?: number
    display?: string
  }) => void
}) {
  const recipesSWR = useRecipes()
  let recipes = recipesSWR.data || []
  const [term, setTerm] = useState('')

  // Courses
  const [course, setCourse] = useLocalStorage<string>('course', 'hoofdgerecht')
  const courses = useMemo(
    () =>
      recipes
        .map((r) => r.course!)
        .filter((c, i, arr) => arr.indexOf(c) === i)
        .filter(Boolean)
        .sort(),
    [recipesSWR.data]
  )
  if (course) recipes = recipes.filter((r) => r.course === course)

  // Switch between fuse and fuzzysort
  const [lib, setLib] = useLocalStorage<'fuse' | 'fuzzysort'>(
    'searchLib',
    'fuzzysort'
  )

  // Fuse
  const enabled = !!term && lib === 'fuse'
  const fuse = useMemo(
    () =>
      recipes && enabled
        ? new Fuse(recipes, {
            keys: ['display', 'main', 'ingredients.product.display'],
            threshold: 0.7,
            includeMatches: true,
          })
        : null,
    [recipes, enabled]
  )
  const input = useRef(null)

  // fuzzysort
  const filtered = fuse
    ? fuse.search(term).map((r) => r.item)
    : term && recipes
      ? fuzzysort
          .go(term, recipes, {
            keys: ['display', 'search'],
            threshold: -10000,
          })
          .map((r) => r.obj)
      : // by plans[0].plannedAt
        recipes?.sort((a, b) => {
          const aDate = a.lastConsumedAt
          const bDate = b.lastConsumedAt
          if (aDate && bDate) {
            return aDate < bDate ? -1 : 1
          }
          if (a.display?.includes('uncomplete')) return 1
          if (b.display?.includes('uncomplete')) return -1
          if (aDate) return -1
          if (bDate) return 1
          return 0
        }) || []

  // focus input on mount
  useEffect(() => {
    const input = document.querySelector('input')
    if (input) input.focus()
  }, [])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex">
        <input
          ref={input}
          type="search"
          placeholder="Zoeken op naam..."
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          className="rounded-full bg-lime-500/30 block w-full px-4 py-2 placeholder:text-lime-700 focus:outline-lime-500"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {term && (
          <div className="flex">
            <button
              type="button"
              className={`rounded-l-full text-sm px-3 py-1 font-medium ${
                lib === 'fuse'
                  ? 'bg-lime-600 text-white'
                  : 'bg-slate-200 text-black'
              }`}
              onClick={() => setLib('fuse')}
            >
              Fuse
            </button>
            <button
              type="button"
              className={`rounded-r-full text-sm px-3 py-1 font-medium ${
                lib === 'fuzzysort'
                  ? 'bg-lime-600 text-white'
                  : 'bg-slate-200 text-black'
              }`}
              onClick={() => setLib('fuzzysort')}
            >
              Fuzzysort
            </button>
          </div>
        )}
        {courses &&
          courses.map((c) => (
            <button
              type="button"
              key={c}
              className={`rounded-full text-sm px-3 py-1 font-medium ${
                c === course
                  ? 'bg-lime-600 text-white'
                  : 'bg-slate-200 text-black'
              }`}
              onClick={() => setCourse((x) => (x === c ? '' : c))}
            >
              {c}
            </button>
          ))}
      </div>
      <div className="flex flex-col ">
        {filtered.map((recipe) => (
          <button
            type="button"
            key={recipe.id}
            className="break-words relative text-left font-medium hover:underline py-2"
            onClick={async () => {
              await onPlan({ recipeId: recipe.id })
              onChange()
              onClose()
            }}
          >
            {recipe.display}
            {recipe.lastConsumedAt && (
              <>
                <span className="text-xs invisible pl-4">
                  {/*  days ago */}
                  {Math.round(
                    (Date.now() - Date.parse(recipe.lastConsumedAt)) /
                      (1000 * 60 * 60 * 24)
                  )}{' '}
                  dagen
                </span>
                <div className="text-xs opacity-60 leading-6 absolute right-0 bottom-2">
                  {Math.round(
                    (Date.now() - Date.parse(recipe.lastConsumedAt)) /
                      (1000 * 60 * 60 * 24)
                  )}{' '}
                  dagen
                </div>
              </>
            )}
          </button>
        ))}

        {term.length > 0 && (
          <button
            type="button"
            className="break-words relative text-left font-medium hover:underline py-2"
            onClick={async () => {
              await onPlan({ display: term })
              onChange()
              onClose()
            }}
          >
            Plan "{term}"
          </button>
        )}
      </div>
      {recipesSWR.error && <div>failed to load {recipesSWR.error.message}</div>}
    </div>
  )
}
